/**
 * Company tax number in the B2B registration form — mirrors backend
 * services/company_registry/tax_id.py so the form can reject a mistyped NIP before asking the
 * registries. Bare digits / "PL…" = Polish NIP (checksum); another EU prefix = VAT number for
 * VIES; any other two-letter prefix = foreign number nobody can verify automatically.
 */
const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

export const EU_VAT_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'HR', 'HU', 'IE', 'IT',
  'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'XI',
]);

export type TaxIdKind = 'nip' | 'eu_vat' | 'foreign';

export interface ParsedTaxId {
  country: string;
  number: string;
  kind: TaxIdKind;
  valid: boolean;
  error?: 'checksum' | 'format';
}

export function nipChecksumOk(digits: string): boolean {
  if (!/^\d{10}$/.test(digits)) return false;
  const sum = NIP_WEIGHTS.reduce((acc, w, i) => acc + w * Number(digits[i]), 0);
  return sum % 11 === Number(digits[9]);
}

export function parseTaxId(raw: string | null | undefined): ParsedTaxId {
  const value = (raw || '').toUpperCase().replace(/[\s\-./]/g, '');
  let prefix = /^[A-Z]{2}/.test(value) ? value.slice(0, 2) : '';
  const number = prefix ? value.slice(2) : value;
  if (prefix === 'GR') prefix = 'EL';

  if (!prefix || prefix === 'PL') {
    if (!/^\d{10}$/.test(number)) return { country: 'PL', number, kind: 'nip', valid: false, error: 'format' };
    const ok = nipChecksumOk(number);
    return { country: 'PL', number, kind: 'nip', valid: ok, ...(ok ? {} : { error: 'checksum' as const }) };
  }
  const plausible = /^[A-Z0-9]{2,14}$/.test(number);
  return {
    country: prefix,
    number,
    kind: EU_VAT_COUNTRIES.has(prefix) ? 'eu_vat' : 'foreign',
    valid: plausible,
    ...(plausible ? {} : { error: 'format' as const }),
  };
}

/** Result of GET /auth/company-lookup and of b2b_data.verification (admin). */
export type CompanyCheckStatus = 'verified' | 'warning' | 'not_found' | 'invalid' | 'unavailable' | 'unverifiable';

export interface CompanyCheck {
  tax_id: string;
  country: string;
  kind: TaxIdKind;
  status: CompanyCheckStatus;
  vat_status?: 'active' | 'exempt' | 'not_registered' | 'eu_valid' | null;
  name?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  address?: string | null;
  regon?: string | null;
  krs?: string | null;
  mf_request_id?: string | null;
  sources?: Record<string, string>;
  checked_at?: string;
  name_matches?: boolean | null;
}
